/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { checkTraineeManagePermission } from "./traineeAuth.js";
import { checkDormitoryMasterDataPermission } from "views/Dormitory/dormitoryAuth.js";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Nav, NavItem, NavLink } from "reactstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/th";

// Icons
import StorageIcon from "@material-ui/icons/Storage";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import SchoolIcon from "@material-ui/icons/School";
import BusinessIcon from "@material-ui/icons/Business";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FilterListIcon from "@material-ui/icons/FilterList";
import SyncIcon from "@material-ui/icons/Sync";
import LinkIcon from "@material-ui/icons/Link";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import HotelIcon from "@material-ui/icons/Hotel";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { DEFAULT_RATES, calculateDormFeeByDays, calculatePaymentBalance } from "./dormitoryFeeCalculator.js";
import {
  ROOM_CATEGORIES,
  ROOM_CATEGORY_LIST,
  getRoomCategoryKey,
  getRoomCategoryInfo,
  applyRoomCategory
} from "views/Dormitory/dormitoryRoomCategoryHelper.js";

moment.locale("th");

const BUILDINGS = [
  { id: 1, name: "อาคารหอพัก 1 (ตึก 1)", shortName: "ตึก 1", totalFloors: 3 },
  { id: 2, name: "อาคารหอพัก 2 (ตึก 2)", shortName: "ตึก 2", totalFloors: 3 },
  { id: 3, name: "อาคารหอพัก 3 (ตึก 3)", shortName: "ตึก 3", totalFloors: 3 },
  { id: 4, name: "อาคารหอพัก 4 (ตึก 4)", shortName: "ตึก 4", totalFloors: 3 }
];

// ข้อมูล Master Data เริ่มต้น (ดึงจากฐานข้อมูลจริง 100%)
const INITIAL_ROOMS_DATA = [];
const INITIAL_INSTITUTIONS = [];
const INITIAL_DEPARTMENTS = [];

const useStyles = makeStyles({
  container: {
    padding: "15px",
    fontFamily: '"Sarabun", sans-serif'
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "0.875rem"
  },
  th: {
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "left"
  },
  thCenter: {
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "center"
  },
  td: {
    padding: "9px 8px",
    borderBottom: "1px solid #e9ecef",
    verticalAlign: "middle"
  }
});

const INITIAL_RATES_CONFIG = {
  building134Monthly: (DEFAULT_RATES && DEFAULT_RATES.BUILDING_1_3_4 && DEFAULT_RATES.BUILDING_1_3_4.MONTHLY) || (DEFAULT_RATES && DEFAULT_RATES.building1_3_4 && DEFAULT_RATES.building1_3_4.monthly) || 500,
  building134Weekly: (DEFAULT_RATES && DEFAULT_RATES.BUILDING_1_3_4 && DEFAULT_RATES.BUILDING_1_3_4.WEEKLY) || (DEFAULT_RATES && DEFAULT_RATES.building1_3_4 && DEFAULT_RATES.building1_3_4.weekly) || 125,
  building134Daily: (DEFAULT_RATES && DEFAULT_RATES.BUILDING_1_3_4 && DEFAULT_RATES.BUILDING_1_3_4.DAILY) || (DEFAULT_RATES && DEFAULT_RATES.building1_3_4 && DEFAULT_RATES.building1_3_4.daily) || 17.86,
  building2DoctorDaily: (DEFAULT_RATES && DEFAULT_RATES.BUILDING_2_DOCTOR && DEFAULT_RATES.BUILDING_2_DOCTOR.DAILY_RATE) || (DEFAULT_RATES && DEFAULT_RATES.building2_doctor && DEFAULT_RATES.building2_doctor.daily) || 100
};

export default function TraineeMasterData({ onlyRoomsTab = false }) {
  const classes = useStyles();
  const history = useHistory();
  const isDormitoryRoute = onlyRoomsTab || (typeof window !== "undefined" && window.location && window.location.pathname.includes("/dormitory"));

  const [activeTab, setActiveTab] = useState(0); // 0 = ห้องพัก, 1 = สถาบัน, 2 = กลุ่มงาน

  useEffect(() => {
    if (isDormitoryRoute && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [isDormitoryRoute, activeTab]);

  const [rooms, setRooms] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLinkedToHospital, setIsLinkedToHospital] = useState(true);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ดึงข้อมูลห้องพักและสถาบันจาก Database API
  const fetchMasterData = async () => {
    try {
      const [roomsRes, instsRes] = await Promise.allSettled([
        axios.get("/api/v1/dormitory/rooms"),
        axios.get("/api/v1/trainee/institutions")
      ]);

      if (!isMountedRef.current) return;

      if (roomsRes.status === "fulfilled" && roomsRes.value?.data?.data && roomsRes.value.data.data.length > 0) {
        const rawRooms = roomsRes.value.data.data;
        const mappedRooms = rawRooms.map(r => {
          const rNum = r.roomNumber || r.RoomNumber || "";
          const bNo = r.buildingNo || r.BuildingNo || (rNum ? rNum.substring(0, 1) : "1");
          const fl = r.floor || r.Floor || (rNum.length >= 2 ? parseInt(rNum.substring(1, 2)) : 1);
          const cap = Number(r.bedCapacity || r.BedCapacity || 2);
          const residents = r.currentResidents || r.CurrentResidents || [];
          const occ = Number(r.currentOccupancy ?? r.CurrentOccupancy ?? residents.length);
          return {
            id: r.roomID || r.RoomID,
            dormitoryId: r.dormitoryID || r.DormitoryID || parseInt(bNo) || 1,
            buildingId: parseInt(bNo) || 1,
            buildingName: `ตึก ${bNo}`,
            roomNo: rNum,
            floor: fl,
            type: r.roomType || r.RoomType || "ทั่วไป",
            roomType: r.roomType || r.RoomType || "ทั่วไป",
            targetGroup: r.targetGroup || r.TargetGroup || "ALL",
            isFamilyRoom: Boolean(r.isFamilyRoom || r.IsFamilyRoom || r.roomType === "ครอบครัว" || r.RoomType === "ครอบครัว"),
            capacity: cap,
            occupancy: occ,
            residents: residents,
            fee: r.monthlyRate || r.MonthlyRate || 500,
            dailyFee: r.dailyRate || r.DailyRate || 100,
            status: r.status || r.Status || "AVAILABLE",
            notes: r.notes || r.Notes || ""
          };
        });
        if (isMountedRef.current) setRooms(mappedRooms);
      }

      if (instsRes.status === "fulfilled" && instsRes.value?.data?.data && instsRes.value.data.data.length > 0) {
        const rawInsts = instsRes.value.data.data;
        const mappedInsts = rawInsts.map(i => ({
          id: i.institutionID || i.InstitutionID,
          name: i.institutionName || i.InstitutionName,
          type: i.institutionType || i.InstitutionType || "สถาบันการศึกษา",
          province: i.province || i.Province || "-",
          count: 0
        }));
        if (isMountedRef.current) setInstitutions(mappedInsts);
      }
    } catch (err) {
      console.warn("Failed to fetch master data from API, using default", err);
    }
  };

  // ดึงข้อมูลกลุ่มงานจากระบบหลักของโรงพยาบาล (เชื่อมโยง dbo.Department / dbo.GroupWork)
  const fetchDepartments = () => {
    if (!isMountedRef.current) return;
    setLoadingDepts(true);
    axios.get("/api/v1/trainee/departments")
      .then(res => {
        if (!isMountedRef.current) return;
        if (res.data && res.data.success && res.data.data && res.data.data.length > 0) {
          setDepartments(res.data.data);
          setIsLinkedToHospital(res.data.isLinkedToHospital !== false);
        }
      })
      .catch(err => {
        console.warn("Failed to fetch departments from API, using default", err);
      })
      .finally(() => {
        if (isMountedRef.current) setLoadingDepts(false);
      });
  };

  useEffect(() => {
    fetchDepartments();
    fetchMasterData();
  }, []);

  // Filters for Rooms
  const [filterBuilding, setFilterBuilding] = useState("ALL");
  const [filterFloor, setFilterFloor] = useState("ALL");

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("ROOM"); // "ROOM", "INST", "DEPT"
  const [isEditing, setIsEditing] = useState(false);
  const [itemForm, setItemForm] = useState({});

  // Filtered Rooms
  const filteredRooms = useMemo(() => {
    return rooms.filter(r => {
      if (filterBuilding !== "ALL" && r.buildingName !== filterBuilding) return false;
      if (filterFloor !== "ALL" && r.floor !== Number(filterFloor)) return false;
      return true;
    });
  }, [rooms, filterBuilding, filterFloor]);

  // Rates & Proration Calculator State
  const [ratesConfig, setRatesConfig] = useState(INITIAL_RATES_CONFIG);
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [tempRates, setTempRates] = useState({ ...ratesConfig });

  const [calcBuilding, setCalcBuilding] = useState("134"); // "134" or "2"
  const [calcMode, setCalcMode] = useState("DAYS"); // "DAYS" or "DATES"
  const [calcDays, setCalcDays] = useState(35); // default 35 days (1 month + 1 week = 625 THB)
  const [calcStartDate, setCalcStartDate] = useState("2025-10-01");
  const [calcEndDate, setCalcEndDate] = useState("2025-11-04");
  const [calcPaidAmount, setCalcPaidAmount] = useState("500");
  const [copySuccess, setCopySuccess] = useState(false);

  const effectiveDays = useMemo(() => {
    if (calcMode === "DAYS") {
      return Math.max(1, parseInt(calcDays, 10) || 1);
    }
    if (!calcStartDate || !calcEndDate) return 1;
    const start = new Date(calcStartDate);
    const end = new Date(calcEndDate);
    const diffTime = end - start;
    if (diffTime < 0) return 1;
    return Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1);
  }, [calcMode, calcDays, calcStartDate, calcEndDate]);

  const calcResult = useMemo(() => {
    const isB2 = calcBuilding === "2";
    if (isB2) {
      const total = effectiveDays * ratesConfig.building2DoctorDaily;
      return {
        isB2: true,
        days: effectiveDays,
        totalDue: total,
        breakdownText: `${effectiveDays} วัน × ${ratesConfig.building2DoctorDaily} บาท/วัน`,
        months: 0,
        weeks: 0,
        remDays: effectiveDays
      };
    } else {
      const months = Math.floor(effectiveDays / 28);
      const remAfterMonths = effectiveDays % 28;
      const weeks = Math.floor(remAfterMonths / 7);
      const remDays = remAfterMonths % 7;
      const total = (months * ratesConfig.building134Monthly) + (weeks * ratesConfig.building134Weekly) + Math.round(remDays * ratesConfig.building134Daily * 100) / 100;

      const parts = [];
      if (months > 0) parts.push(`${months} เดือน (${(months * ratesConfig.building134Monthly).toLocaleString()} บ.)`);
      if (weeks > 0) parts.push(`${weeks} สัปดาห์ (${(weeks * ratesConfig.building134Weekly).toLocaleString()} บ.)`);
      if (remDays > 0) parts.push(`${remDays} วัน (${(remDays * ratesConfig.building134Daily).toFixed(2)} บ.)`);

      return {
        isB2: false,
        days: effectiveDays,
        totalDue: total,
        breakdownText: parts.join(" + ") || `${effectiveDays} วัน`,
        months,
        weeks,
        remDays
      };
    }
  }, [calcBuilding, effectiveDays, ratesConfig]);

  const balanceResult = useMemo(() => {
    const paid = parseFloat(calcPaidAmount) || 0;
    const total = calcResult.totalDue;
    const remaining = total - paid;
    return {
      paid,
      remaining: Math.max(0, remaining),
      overpaid: remaining < 0 ? Math.abs(remaining) : 0,
      isFullyPaid: remaining === 0 && paid > 0,
      isPartial: paid > 0 && remaining > 0,
      isUnpaid: paid === 0
    };
  }, [calcResult, calcPaidAmount]);

  const handleCopySummary = () => {
    const isB2 = calcBuilding === "2";
    const buildingName = isB2 ? "อาคาร 2 (หอพักแพทย์)" : "อาคาร 1, 3, 4 (นศ.ฝึกงาน / บุคลากร)";
    const dateRange = calcMode === "DATES" ? ` (${calcStartDate} ถึง ${calcEndDate})` : "";
    const text = `[ใบแจ้งยอดค่าบำรุงหอพัก รพ.โรคผิวหนังเขตร้อนภาคใต้ จ.ตรัง]
• อาคาร: ${buildingName}
• ระยะเวลา: ${effectiveDays} วัน${dateRange}
• วิธีคำนวณ: ${calcResult.breakdownText}
• ยอดเรียกเก็บรวม: ${calcResult.totalDue.toLocaleString()} บาท
• ยอดเงินที่ชำระแล้ว: ${balanceResult.paid.toLocaleString()} บาท
• สถานะ: ${balanceResult.isFullyPaid ? "✓ ชำระครบถ้วนแล้ว (ค้าง 0 บาท)" : balanceResult.isPartial ? `⚠️ ค้างชำระอีก ${balanceResult.remaining.toLocaleString()} บาท` : `⏳ ยังไม่ชำระ (ค้าง ${balanceResult.remaining.toLocaleString()} บาท)`}${balanceResult.overpaid > 0 ? ` (ชำระเกิน ทอน ${balanceResult.overpaid.toLocaleString()} บาท)` : ""}
`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    if (activeTab === 0) {
      setModalMode("ROOM");
      setItemForm({
        buildingId: 1,
        buildingName: "ตึก 1",
        roomNo: "",
        floor: 1,
        capacity: 2,
        fee: 0,
        dailyFee: 0,
        type: "ทั่วไป",
        roomType: "ทั่วไป",
        targetGroup: "STAFF",
        isFamilyRoom: false,
        status: "AVAILABLE"
      });
    } else if (activeTab === 1) {
      setModalMode("INST");
      setItemForm({ name: "", type: "มหาวิทยาลัย", province: "ตรัง" });
    } else {
      setModalMode("DEPT");
      setItemForm({
        code: `DEPT-${departments.length + 1}`,
        name: "",
        shortName: "",
        groupWorkName: "กลุ่มภารกิจด้านบริการทางการแพทย์",
        color: "#3f51b5"
      });
    }
    setModalOpen(true);
  };

  const handleOpenEditRoom = (room) => {
    setIsEditing(true);
    setModalMode("ROOM");
    setItemForm({ ...room });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (modalMode === "ROOM") {
      if (!itemForm.roomNo) {
        alert("กรุณาระบุเลขห้องพัก");
        return;
      }
      const occ = Number(itemForm.occupancy ?? (itemForm.residents ? itemForm.residents.length : 0));
      const cap = Number(itemForm.capacity || 2);
      if (itemForm.status === "MAINTENANCE" && occ > 0) {
        alert(
          `⚠️ ไม่สามารถปิดปรับปรุงได้ในขณะนี้\n\n` +
          `ห้อง ${itemForm.roomNo || ""} ยังมีผู้พักอาศัยอยู่จำนวน ${occ} คน\n` +
          `กรุณาดำเนินการกดย้ายออก (Check-out) ให้เรียบร้อยก่อน เพื่อเช็คว่าปิดปรับปรุงจริงไหม`
        );
        return;
      }

      const bObj = BUILDINGS.find(b => b.shortName === itemForm.buildingName) || BUILDINGS[0];
      const cat = getRoomCategoryInfo(itemForm);
      const isStudentOrDoctor = cat.key === "STUDENT" || cat.key === "DOCTOR";
      const isFamily = cat.key === "STAFF_FAMILY";
      const finalStatus = itemForm.status === "MAINTENANCE" ? "MAINTENANCE" : (occ >= cap ? "OCCUPIED" : "AVAILABLE");
      const roomPayload = {
        ...itemForm,
        type: cat.roomType,
        roomType: cat.roomType,
        targetGroup: cat.targetGroup,
        isFamilyRoom: isFamily,
        status: finalStatus,
        fee: isStudentOrDoctor ? (Number(itemForm.fee) || 500) : 0,
        dailyFee: isStudentOrDoctor ? (Number(itemForm.dailyFee) || 100) : 0,
        buildingId: bObj.id,
        buildingName: itemForm.buildingName || "ตึก 1"
      };

      if (isEditing) {
        try {
          if (itemForm.id && typeof itemForm.id === "number") {
            await axios.put(`/api/v1/dormitory/rooms/${itemForm.id}`, {
              roomID: itemForm.id,
              dormitoryID: itemForm.dormitoryId || bObj.id,
              buildingNo: String(bObj.id),
              buildingName: `อาคารหอพัก ${bObj.id}`,
              roomNumber: String(itemForm.roomNo),
              floor: Number(itemForm.floor) || 1,
              bedCapacity: cap,
              targetGroup: cat.targetGroup,
              roomType: cat.roomType,
              isFamilyRoom: isFamily,
              monthlyRate: Number(roomPayload.fee) || 0,
              dailyRate: Number(roomPayload.dailyFee) || 0,
              status: finalStatus,
              notes: itemForm.notes || ""
            });
          }
        } catch (apiErr) {
          console.warn("API update failed, updating local state only:", apiErr);
        }
        setRooms(prev => prev.map(r => r.id === itemForm.id ? roomPayload : r));
      } else {
        setRooms(prev => [...prev, { ...roomPayload, id: Date.now() }]);
      }
    } else if (modalMode === "INST") {
      if (!itemForm.name) return;
      setInstitutions((prev) => [...prev, { ...itemForm, id: Date.now(), count: 0 }]);
    } else {
      if (!itemForm.name) return;
      const deptPayload = {
        ...itemForm,
        id: itemForm.id || Date.now(),
        departmentKey: itemForm.departmentKey || Date.now(),
        code: itemForm.code || `DEPT-${departments.length + 1}`,
        name: itemForm.name,
        shortName: itemForm.shortName || itemForm.name,
        groupWorkName: itemForm.groupWorkName || "กลุ่มภารกิจด้านบริการทางการแพทย์",
        color: itemForm.color || "#3f51b5",
        activeDoctorCount: itemForm.activeDoctorCount || 0,
        activeStudentCount: itemForm.activeStudentCount || 0,
        totalActiveTrainees: (itemForm.activeDoctorCount || 0) + (itemForm.activeStudentCount || 0),
        isLinkedToHospital: true
      };
      if (isEditing) {
        setDepartments((prev) => prev.map((d) => (d.id === deptPayload.id ? deptPayload : d)));
      } else {
        setDepartments((prev) => [...prev, deptPayload]);
      }
    }
    setModalOpen(false);
  };

  const handleDelete = (id, mode) => {
    if (window.confirm("คุณต้องการลบข้อมูลรายการนี้ใช่หรือไม่?")) {
      if (mode === "ROOM") setRooms((prev) => prev.filter((r) => r.id !== id));
      if (mode === "INST") setInstitutions((prev) => prev.filter((i) => i.id !== id));
      if (mode === "DEPT") setDepartments((prev) => prev.filter((d) => d.id !== id));
    }
  };

  // ป้องกันการเข้าถึงโดยตรง หากไม่มีสิทธิ์ (ตรวจสอบหลังจากประกาศ React Hooks ทั้งหมดครบถ้วนตาม Rules of Hooks)
  if (isDormitoryRoute) {
    if (!checkDormitoryMasterDataPermission()) {
      return <Redirect to="/dormitory/manage" />;
    }
  } else {
    if (!checkTraineeManagePermission()) {
      return <Redirect to="/trainee/dashboard" />;
    }
  }

  return (
    <div className={classes.container}>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#37474f", flexWrap: "wrap" }}>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontWeight: "bold" }}>
                  <StorageIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />
                  {isDormitoryRoute ? "จัดการข้อมูลหลักห้องพักหอพัก (Master Data)" : "จัดการข้อมูลหลัก (Master Data Management)"}
                </h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: "4px 0 0 0", fontSize: "0.88rem" }}>
                  {isDormitoryRoute
                    ? "จัดการเลขห้องพักและข้อมูลอาคารหอพัก"
                    : "จัดการเลขห้องพักหอพัก, สถาบันการศึกษา และกลุ่มงาน"}
                </p>
              </div>
              <div style={{ marginTop: "8px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                {isDormitoryRoute ? (
                  <Button color="info" size="sm" onClick={() => history.push("/dormitory/manage")}>
                    ← กลับไปหน้าผังหอพัก
                  </Button>
                ) : (
                  <Button color="info" size="sm" onClick={() => history.push("/trainee/dormitory")}>
                    ← กลับไปหน้าผังหอพัก
                  </Button>
                )}
                {/* ปุ่มเพิ่มห้องพักซ่อนไว้ตามคำสั่ง เนื่องจากผังห้องพักถูก fix ตามผังจริงแล้ว คงไว้เฉพาะการแก้ไขข้อมูลห้องพัก */}
                {activeTab !== 0 && activeTab !== 3 && (
                  <Button color="success" size="sm" onClick={handleOpenAdd}>
                    <AddIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                    {activeTab === 1 && "+ เพิ่มสถาบันการศึกษา"}
                    {activeTab === 2 && "+ เพิ่มกลุ่มงาน"}
                  </Button>
                )}
              </div>
            </CardHeader>

            {/* Nav Tabs */}
            <div style={{ borderBottom: "2px solid #e0e0e0", backgroundColor: "#fafafa", padding: "10px 15px 0 15px" }}>
              <Nav tabs style={{ borderBottom: "none" }}>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 0 ? "bold" : "normal",
                      color: activeTab === 0 ? "#37474f" : "#555",
                      borderBottom: activeTab === 0 ? "3px solid #37474f" : "none",
                      backgroundColor: activeTab === 0 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => setActiveTab(0)}
                  >
                    <MeetingRoomIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                    จัดการห้องพักและอาคาร
                  </NavLink>
                </NavItem>
                {!isDormitoryRoute && (
                  <>
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === 1 ? "bold" : "normal",
                          color: activeTab === 1 ? "#37474f" : "#555",
                          borderBottom: activeTab === 1 ? "3px solid #37474f" : "none",
                          backgroundColor: activeTab === 1 ? "#fff" : "transparent",
                          fontSize: "1rem",
                          padding: "10px 20px"
                        }}
                        onClick={() => setActiveTab(1)}
                      >
                        <SchoolIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ข้อมูลสถาบันการศึกษา / ต้นสังกัด ({institutions.length})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === 2 ? "bold" : "normal",
                          color: activeTab === 2 ? "#37474f" : "#555",
                          borderBottom: activeTab === 2 ? "3px solid #37474f" : "none",
                          backgroundColor: activeTab === 2 ? "#fff" : "transparent",
                          fontSize: "1rem",
                          padding: "10px 20px"
                        }}
                        onClick={() => setActiveTab(2)}
                      >
                        <BusinessIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ข้อมูลกลุ่มงานใน รพ. ({departments.length})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === 3 ? "bold" : "normal",
                          color: activeTab === 3 ? "#37474f" : "#555",
                          borderBottom: activeTab === 3 ? "3px solid #37474f" : "none",
                          backgroundColor: activeTab === 3 ? "#fff" : "transparent",
                          fontSize: "1rem",
                          padding: "10px 20px"
                        }}
                        onClick={() => setActiveTab(3)}
                      >
                        <AttachMoneyIcon style={{ verticalAlign: "middle", marginRight: "6px", color: "#16a34a" }} />
                        เรทราคาและค่าบำรุงหอพัก (Rates & Calculator)
                      </NavLink>
                    </NavItem>
                  </>
                )}
              </Nav>
            </div>

            <CardBody>
              {/* Tab 0: Rooms Management with 4 Buildings x 3 Floors */}
              {activeTab === 0 && (
                <div>
                  {/* Building & Floor Filter Toolbar */}
                  <div className="d-flex flex-wrap align-items-center justify-content-between p-2 mb-3" style={{ backgroundColor: "#eceff1", borderRadius: "6px" }}>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="mr-2 text-muted" style={{ fontSize: "0.88rem" }}>
                        <FilterListIcon style={{ fontSize: "1.1rem", verticalAlign: "middle" }} /> กรองตามอาคาร:
                      </span>
                      {["ALL", "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"].map(b => (
                        <button
                          key={b}
                          type="button"
                          className={`btn btn-sm mr-1 mb-1 ${filterBuilding === b ? "btn-dark font-weight-bold" : "btn-outline-secondary"}`}
                          style={{ fontSize: "0.82rem", borderRadius: "16px" }}
                          onClick={() => setFilterBuilding(b)}
                        >
                          {b === "ALL" ? "🏢 ทุกอาคาร" : `🏢 ${b}`}
                        </button>
                      ))}
                    </div>

                    <div className="d-flex align-items-center mt-2 mt-md-0">
                      <span className="mr-2 text-muted" style={{ fontSize: "0.88rem" }}>ชั้น:</span>
                      <select
                        className="form-control form-control-sm"
                        style={{ width: "120px" }}
                        value={filterFloor}
                        onChange={(e) => setFilterFloor(e.target.value)}
                      >
                        <option value="ALL">ทุกชั้น (1-3)</option>
                        <option value="1">ชั้น 1</option>
                        <option value="2">ชั้น 2</option>
                        <option value="3">ชั้น 3</option>
                      </select>
                    </div>
                  </div>

                  {/* Rooms Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.thCenter} style={{ width: "60px" }}>ลำดับ</th>
                          <th className={classes.th}>อาคาร / ตึก</th>
                          <th className={classes.thCenter} style={{ width: "80px" }}>ชั้น</th>
                          <th className={classes.th}>หมายเลขห้อง</th>
                          <th className={classes.thCenter}>ความจุเตียง</th>
                          <th className={classes.th}>ประเภทห้อง</th>
                          <th className={classes.thCenter}>สถานะ</th>
                          <th className={classes.thCenter} style={{ width: "120px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRooms.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4 text-muted">
                              ไม่พบข้อมูลห้องพักตามเงื่อนไขที่เลือก
                            </td>
                          </tr>
                        ) : (
                          filteredRooms.map((room, idx) => (
                            <tr key={room.id} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                              <td className={classes.td} style={{ textAlign: "center" }}>{idx + 1}</td>
                              <td className={classes.td} style={{ fontWeight: "bold", color: "#37474f" }}>
                                🏢 {room.buildingName}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Badge color="secondary">ชั้น {room.floor}</Badge>
                              </td>
                              <td className={classes.td} style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                                ห้อง {room.roomNo}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {room.capacity} เตียง
                              </td>
                              <td className={classes.td}>
                                {(() => {
                                  const catInfo = getRoomCategoryInfo(room);
                                  return (
                                    <span
                                      style={{
                                        fontSize: "0.78rem",
                                        padding: "3px 9px",
                                        borderRadius: "6px",
                                        backgroundColor: catInfo.badgeBg,
                                        color: catInfo.badgeColor,
                                        border: `1.5px solid ${catInfo.badgeBorder}`,
                                        fontWeight: "700",
                                        display: "inline-block",
                                        whiteSpace: "nowrap"
                                      }}
                                    >
                                      {catInfo.fullTag}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {(() => {
                                  if (room.status === "MAINTENANCE") {
                                    return (
                                      <Badge
                                        color="secondary"
                                        style={{
                                          backgroundColor: "#78909c",
                                          color: "#fff",
                                          padding: "5px 10px",
                                          fontSize: "0.85rem",
                                          borderRadius: "6px"
                                        }}
                                      >
                                        🔧 ปิดปรับปรุง
                                      </Badge>
                                    );
                                  }
                                  const occ = Number(room.occupancy ?? (room.residents ? room.residents.length : 0));
                                  const cap = Number(room.capacity || 2);
                                  if (occ === 0) {
                                    return (
                                      <Badge
                                        color="success"
                                        style={{
                                          backgroundColor: "#2e7d32",
                                          color: "#fff",
                                          padding: "5px 12px",
                                          fontSize: "0.85rem",
                                          borderRadius: "6px"
                                        }}
                                      >
                                        ว่าง
                                      </Badge>
                                    );
                                  } else if (occ < cap) {
                                    const remaining = cap - occ;
                                    return (
                                      <Badge
                                        color="warning"
                                        style={{
                                          backgroundColor: "#ed6c02",
                                          color: "#fff",
                                          padding: "5px 10px",
                                          fontSize: "0.85rem",
                                          borderRadius: "6px"
                                        }}
                                      >
                                        ว่างอีก {remaining} คน
                                      </Badge>
                                    );
                                  } else {
                                    return (
                                      <Badge
                                        color="danger"
                                        style={{
                                          backgroundColor: "#d32f2f",
                                          color: "#fff",
                                          padding: "5px 12px",
                                          fontSize: "0.85rem",
                                          borderRadius: "6px"
                                        }}
                                      >
                                        ไม่ว่าง
                                      </Badge>
                                    );
                                  }
                                })()}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Button
                                  color="info"
                                  simple
                                  justIcon
                                  size="sm"
                                  onClick={() => handleOpenEditRoom(room)}
                                  title="แก้ไขห้องพัก"
                                >
                                  <EditIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 1: Institutions */}
              {activeTab === 1 && (
                <div style={{ overflowX: "auto" }}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.thCenter} style={{ width: "60px" }}>ลำดับ</th>
                        <th className={classes.th}>ชื่อสถาบันการศึกษา / หน่วยงาน</th>
                        <th className={classes.th}>ประเภท</th>
                        <th className={classes.th}>จังหวัด</th>
                        <th className={classes.thCenter}>จำนวนผู้มาฝึกสะสม</th>
                        <th className={classes.thCenter} style={{ width: "100px" }}>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutions.map((inst, idx) => (
                        <tr key={inst.id}>
                          <td className={classes.td} style={{ textAlign: "center" }}>{idx + 1}</td>
                          <td className={classes.td} style={{ fontWeight: "bold" }}>{inst.name}</td>
                          <td className={classes.td}>{inst.type}</td>
                          <td className={classes.td}>{inst.province}</td>
                          <td className={classes.td} style={{ textAlign: "center" }}>
                            <Badge color="info" pill>{inst.count} คน</Badge>
                          </td>
                          <td className={classes.td} style={{ textAlign: "center" }}>
                            <Button
                              color="danger"
                              simple
                              justIcon
                              size="sm"
                              onClick={() => handleDelete(inst.id, "INST")}
                            >
                              <DeleteIcon style={{ fontSize: "1.1rem" }} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab 2: Departments (เชื่อมโยงกับ dbo.Department ของ รพ.) */}
              {activeTab === 2 && (
                <div>
                  {/* Connection Banner */}
                  <div
                    className="d-flex justify-content-between align-items-center p-3 mb-3"
                    style={{
                      backgroundColor: "#e8f5e9",
                      borderLeft: "5px solid #2e7d32",
                      borderRadius: "6px"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "bold", color: "#1b5e20", fontSize: "0.95rem" }}>
                        <LinkIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        เชื่อมโยงกับฐานข้อมูลกลุ่มงานหลักของโรงพยาบาลแล้ว (dbo.Department & dbo.GroupWork)
                      </div>
                      <div style={{ color: "#2e7d32", fontSize: "0.83rem", marginTop: "2px" }}>
                        ข้อมูลกลุ่มงานและสายงานภารกิจเชื่อมตรงกับระบบ BackOffice ช่วยให้ข้อมูลผู้เข้าฝึกงานสอดคล้องกับโครงสร้าง รพ. ทั้งหมด
                      </div>
                    </div>
                    <div>
                      <Button
                        color="success"
                        size="sm"
                        style={{ fontWeight: "bold" }}
                        onClick={fetchDepartments}
                        disabled={loadingDepts}
                      >
                        <SyncIcon
                          style={{
                            fontSize: "1.1rem",
                            marginRight: "4px",
                            verticalAlign: "middle",
                            animation: loadingDepts ? "spin 1s linear infinite" : "none"
                          }}
                        />
                        {loadingDepts ? "กำลังซิงค์..." : "ซิงค์ข้อมูล รพ."}
                      </Button>
                    </div>
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.thCenter} style={{ width: "50px" }}>ลำดับ</th>
                          <th className={classes.th} style={{ width: "100px" }}>รหัสกลุ่มงาน</th>
                          <th className={classes.th}>ชื่อกลุ่มงานในโรงพยาบาล</th>
                          <th className={classes.th}>กลุ่มภารกิจ / สายงานสังกัด</th>
                          <th className={classes.thCenter}>สีสัญลักษณ์</th>
                          <th className={classes.thCenter}>แพทย์ฝึกอบรม</th>
                          <th className={classes.thCenter}>นศ. ฝึกงาน</th>
                          <th className={classes.thCenter}>รวมผู้ฝึกงาน</th>
                          <th className={classes.thCenter} style={{ width: "150px" }}>สถานะในระบบ รพ.</th>
                          <th className={classes.thCenter} style={{ width: "90px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.map((dept, idx) => (
                          <tr key={dept.id || dept.departmentKey || idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafcff" }}>
                            <td className={classes.td} style={{ textAlign: "center", color: "#666" }}>{idx + 1}</td>
                            <td className={classes.td} style={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#00695c" }}>
                              {dept.code || `DEPT-${dept.departmentKey || idx + 1}`}
                            </td>
                            <td className={classes.td}>
                              <span style={{ fontWeight: "bold", color: "#222" }}>{dept.name}</span>
                              {dept.shortName && dept.shortName !== dept.name && (
                                <span style={{ color: "#777", fontSize: "0.82rem", marginLeft: "6px" }}>
                                  ({dept.shortName})
                                </span>
                              )}
                            </td>
                            <td className={classes.td} style={{ fontSize: "0.85rem", color: "#555" }}>
                              {dept.groupWorkName || "กลุ่มภารกิจหลัก"}
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "20px",
                                  height: "20px",
                                  backgroundColor: dept.color || "#00838f",
                                  borderRadius: "4px",
                                  verticalAlign: "middle",
                                  border: "1px solid rgba(0,0,0,0.1)"
                                }}
                              />
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <Badge color="info" pill style={{ fontSize: "0.8rem", padding: "4px 8px" }}>
                                {dept.activeDoctorCount || 0} คน
                              </Badge>
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <Badge color="success" pill style={{ fontSize: "0.8rem", padding: "4px 8px" }}>
                                {dept.activeStudentCount || 0} คน
                              </Badge>
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <Badge color="dark" pill style={{ fontSize: "0.82rem", padding: "4px 9px", fontWeight: "bold" }}>
                                {dept.totalActiveTrainees || dept.activeCount || (dept.activeDoctorCount || 0) + (dept.activeStudentCount || 0)} คน
                              </Badge>
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <Badge color="success" style={{ fontSize: "0.75rem", padding: "4px 6px" }}>
                                <CheckCircleOutlineIcon style={{ fontSize: "0.9rem", verticalAlign: "middle", marginRight: "2px" }} />
                                เชื่อมโยง รพ. แล้ว
                              </Badge>
                            </td>
                            <td className={classes.td} style={{ textAlign: "center" }}>
                              <Button
                                color="danger"
                                simple
                                justIcon
                                size="sm"
                                title="ลบออกจากรายการแสดง"
                                onClick={() => handleDelete(dept.id, "DEPT")}
                              >
                                <DeleteIcon style={{ fontSize: "1.1rem" }} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 3: Rates & Calculator */}
              {activeTab === 3 && (
                <div>
                  {/* Alert Banner */}
                  <div
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "10px",
                      padding: "14px 18px",
                      marginBottom: "20px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                      <div>
                        <h6 style={{ margin: 0, color: "#166534", fontWeight: "800", fontSize: "1rem" }}>
                          <AttachMoneyIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                          อัตราค่าบำรุงหอพักโรงพยาบาลและการคำนวณตามสัดส่วน (Dormitory Rates & Proration)
                        </h6>
                        <p style={{ margin: "4px 0 0", color: "#15803d", fontSize: "0.86rem" }}>
                          ใช้สำหรับคำนวณค่าบำรุงหอพักของแพทย์และนักศึกษาฝึกงาน ติดตามการชำระเงิน และคำนวณยอดค้างชำระ
                        </p>
                      </div>
                      <div>
                        {!isEditingRates ? (
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm font-weight-bold"
                            style={{ borderRadius: "8px" }}
                            onClick={() => {
                              setTempRates({ ...ratesConfig });
                              setIsEditingRates(true);
                            }}
                          >
                            <EditIcon style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }} />
                            แก้ไขเรทราคามาตรฐาน
                          </button>
                        ) : (
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              style={{ borderRadius: "8px" }}
                              onClick={() => setIsEditingRates(false)}
                            >
                              ยกเลิก
                            </button>
                            <button
                              type="button"
                              className="btn btn-success btn-sm font-weight-bold"
                              style={{ borderRadius: "8px" }}
                              onClick={() => {
                                setRatesConfig({ ...tempRates });
                                setIsEditingRates(false);
                              }}
                            >
                              บันทึกเรทใหม่
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: 2 Rate Master Cards */}
                  <div className="row mb-4">
                    {/* Card A: Building 1, 3, 4 */}
                    <div className="col-lg-6 mb-3">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: "2px solid #0284c7",
                          borderRadius: "14px",
                          padding: "20px",
                          height: "100%",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", borderBottom: "1px solid #e0f2fe", paddingBottom: "10px" }}>
                          <div>
                            <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", padding: "4px 10px", borderRadius: "12px", fontSize: "0.78rem", fontWeight: "800" }}>
                              ตึก 1, ตึก 3, ตึก 4
                            </span>
                            <h5 style={{ margin: "6px 0 0", fontWeight: "800", color: "#0c4a6e" }}>
                              🏢 นักศึกษาฝึกงาน / สหสาขาวิชาชีพ
                            </h5>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "1.4rem", fontWeight: "900", color: "#0284c7" }}>
                              {ratesConfig.building134Monthly.toLocaleString()} ฿
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600" }}>
                              ต่อคน / เดือน (4 สัปดาห์)
                            </div>
                          </div>
                        </div>

                        {/* Rate breakdown table */}
                        <table className="table table-bordered table-sm mb-3" style={{ fontSize: "0.86rem" }}>
                          <thead style={{ backgroundColor: "#f0f9ff", color: "#0369a1" }}>
                            <tr>
                              <th style={{ fontWeight: "800" }}>ระยะเวลา</th>
                              <th style={{ fontWeight: "800" }}>วิธีคำนวณ</th>
                              <th style={{ fontWeight: "800", textAlign: "right" }}>จำนวนเงิน (บาท)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><b>1 เดือน</b> (4 สัปดาห์ / 28 วัน)</td>
                              <td>ค่าใช้จ่ายตั้งต้นฐาน</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#0369a1" }}>
                                {isEditingRates ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-right"
                                    value={tempRates.building134Monthly}
                                    onChange={(e) => {
                                      const m = Number(e.target.value) || 0;
                                      const w = Math.round((m / 4) * 100) / 100;
                                      const d = Math.round((w / 7) * 100) / 100;
                                      setTempRates({ ...tempRates, building134Monthly: m, building134Weekly: w, building134Daily: d });
                                    }}
                                  />
                                ) : (
                                  `${ratesConfig.building134Monthly.toFixed(2)}`
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td><b>1 สัปดาห์</b> (7 วัน)</td>
                              <td>ค่ารายเดือน ÷ 4</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#0369a1" }}>
                                {isEditingRates ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-right"
                                    value={tempRates.building134Weekly}
                                    onChange={(e) => setTempRates({ ...tempRates, building134Weekly: Number(e.target.value) || 0 })}
                                  />
                                ) : (
                                  `${ratesConfig.building134Weekly.toFixed(2)}`
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td><b>1 วันเศษ</b></td>
                              <td>ค่ารายสัปดาห์ ÷ 7</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#0369a1" }}>
                                {isEditingRates ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-right"
                                    value={tempRates.building134Daily}
                                    onChange={(e) => setTempRates({ ...tempRates, building134Daily: Number(e.target.value) || 0 })}
                                  />
                                ) : (
                                  `ตกวันละ ${ratesConfig.building134Daily.toFixed(2)}`
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "10px 12px", border: "1px dashed #cbd5e1" }}>
                          <span style={{ fontSize: "0.82rem", color: "#0f172a", fontWeight: "700" }}>
                            💡 ตัวอย่างกรณีศึกษาตามระเบียบ:
                          </span>
                          <ul style={{ margin: "4px 0 0", paddingLeft: "20px", fontSize: "0.82rem", color: "#334155" }}>
                            <li><b>1 เดือน + 1 สัปดาห์ (35 วัน):</b> 500.00 + 125.00 = <b>625.00 บาท</b></li>
                            <li><b>2 เดือนพอดี (56-60 วัน):</b> 500.00 × 2 = <b>1,000.00 บาท</b></li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Card B: Building 2 (Doctors) */}
                    <div className="col-lg-6 mb-3">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: "2px solid #059669",
                          borderRadius: "14px",
                          padding: "20px",
                          height: "100%",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", borderBottom: "1px solid #d1fae5", paddingBottom: "10px" }}>
                          <div>
                            <span style={{ backgroundColor: "#d1fae5", color: "#065f46", padding: "4px 10px", borderRadius: "12px", fontSize: "0.78rem", fontWeight: "800" }}>
                              ตึก 2 (หอพักแพทย์)
                            </span>
                            <h5 style={{ margin: "6px 0 0", fontWeight: "800", color: "#064e3b" }}>
                              🩺 แพทย์ฝึกอบรม / แพทย์ Elective
                            </h5>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "1.4rem", fontWeight: "900", color: "#059669" }}>
                              {ratesConfig.building2DoctorDaily.toLocaleString()} ฿
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600" }}>
                              ต่อคน / วัน (นับตามวันจริง)
                            </div>
                          </div>
                        </div>

                        {/* Rate table */}
                        <table className="table table-bordered table-sm mb-3" style={{ fontSize: "0.86rem" }}>
                          <thead style={{ backgroundColor: "#ecfdf5", color: "#065f46" }}>
                            <tr>
                              <th style={{ fontWeight: "800" }}>เกณฑ์การคิดค่าบำรุง</th>
                              <th style={{ fontWeight: "800" }}>สูตรคำนวณ</th>
                              <th style={{ fontWeight: "800", textAlign: "right" }}>จำนวนเงิน (บาท)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><b>อัตราเหมารายวัน</b></td>
                              <td>นับจำนวนวันจริงที่เข้าพัก</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#059669" }}>
                                {isEditingRates ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-right"
                                    value={tempRates.building2DoctorDaily}
                                    onChange={(e) => setTempRates({ ...tempRates, building2DoctorDaily: Number(e.target.value) || 0 })}
                                  />
                                ) : (
                                  `วันละ ${ratesConfig.building2DoctorDaily.toFixed(2)}`
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td><b>2 สัปดาห์ (14 วัน)</b></td>
                              <td>14 วัน × 100 บาท</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#059669" }}>
                                1,400.00
                              </td>
                            </tr>
                            <tr>
                              <td><b>1 เดือน (30 วัน)</b></td>
                              <td>30 วัน × 100 บาท</td>
                              <td style={{ textAlign: "right", fontWeight: "800", color: "#059669" }}>
                                3,000.00
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "10px 12px", border: "1px dashed #cbd5e1" }}>
                          <span style={{ fontSize: "0.82rem", color: "#0f172a", fontWeight: "700" }}>
                            💡 หมายเหตุแพทย์ตึก 2:
                          </span>
                          <ul style={{ margin: "4px 0 0", paddingLeft: "20px", fontSize: "0.82rem", color: "#334155" }}>
                            <li>แพทย์ Elective คิดเหมาเป็นรายวัน วันละ 100.00 บาท/คน</li>
                            <li>แพทย์ Fellowship หรือแพทย์ประจำบ้านอาจได้รับการยกเว้นตามประกาศสถาบัน</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Interactive Live Proration & Partial Payment Calculator */}
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      border: "2px solid #6366f1",
                      borderRadius: "14px",
                      padding: "22px",
                      boxShadow: "0 4px 14px rgba(99, 102, 241, 0.08)"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "18px", borderBottom: "2px solid #e0e7ff", paddingBottom: "12px" }}>
                      <div>
                        <h5 style={{ margin: 0, fontWeight: "900", color: "#312e81" }}>
                          🧮 เครื่องคำนวณค่าบำรุงหอพักและยอดค้างชำระ (Interactive Fee & Balance Calculator)
                        </h5>
                        <p style={{ margin: "4px 0 0", color: "#4338ca", fontSize: "0.88rem" }}>
                          เครื่องมือสำหรับเจ้าหน้าที่การเงินและฝ่ายบริหาร ใช้คำนวณยอดเงินตามวันจริงและตรวจสอบยอดค้างชำระ
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary font-weight-bold"
                          style={{ borderRadius: "8px" }}
                          onClick={handleCopySummary}
                        >
                          <FileCopyIcon style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }} />
                          {copySuccess ? "✓ คัดลอกข้อความสรุปแล้ว!" : "คัดลอกข้อความสรุป (แจ้งน้อง/การเงิน)"}
                        </button>
                      </div>
                    </div>

                    <div className="row">
                      {/* Left: Input controls */}
                      <div className="col-lg-6 mb-3">
                        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "16px" }}>
                          <h6 style={{ fontWeight: "800", color: "#0f172a", marginBottom: "12px" }}>
                            1. กำหนดข้อมูลการเข้าพัก
                          </h6>

                          {/* Building Selector */}
                          <div className="mb-3">
                            <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                              ประเภทผู้เข้าพัก / อาคารหอพัก:
                            </label>
                            <div className="d-flex" style={{ gap: "10px" }}>
                              <button
                                type="button"
                                className={`btn flex-fill ${calcBuilding === "134" ? "btn-primary font-weight-bold" : "btn-outline-secondary"}`}
                                style={{ borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}
                                onClick={() => setCalcBuilding("134")}
                              >
                                ตึก 1, 3, 4 (นศ.ฝึกงาน)
                              </button>
                              <button
                                type="button"
                                className={`btn flex-fill ${calcBuilding === "2" ? "btn-success font-weight-bold" : "btn-outline-secondary"}`}
                                style={{ borderRadius: "8px", padding: "8px 12px", fontSize: "0.85rem" }}
                                onClick={() => setCalcBuilding("2")}
                              >
                                ตึก 2 (แพทย์ Elective วันละ 100฿)
                              </button>
                            </div>
                          </div>

                          {/* Calculation Mode: Days or Dates */}
                          <div className="mb-3">
                            <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                              วิธีระบุระยะเวลา:
                            </label>
                            <div className="d-flex" style={{ gap: "10px" }}>
                              <button
                                type="button"
                                className={`btn btn-sm flex-fill ${calcMode === "DAYS" ? "btn-dark font-weight-bold" : "btn-outline-secondary"}`}
                                style={{ borderRadius: "6px" }}
                                onClick={() => setCalcMode("DAYS")}
                              >
                                🔢 ระบุจำนวนวันโดยตรง
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm flex-fill ${calcMode === "DATES" ? "btn-dark font-weight-bold" : "btn-outline-secondary"}`}
                                style={{ borderRadius: "6px" }}
                                onClick={() => setCalcMode("DATES")}
                              >
                                📅 เลือกวันที่เข้าพัก - สิ้นสุด
                              </button>
                            </div>
                          </div>

                          {calcMode === "DAYS" ? (
                            <div className="mb-3">
                              <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                                จำนวนวันเข้าพักทั้งหมด:
                              </label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  min="1"
                                  className="form-control font-weight-bold"
                                  style={{ fontSize: "1.1rem" }}
                                  value={calcDays}
                                  onChange={(e) => setCalcDays(e.target.value)}
                                />
                                <div className="input-group-append">
                                  <span className="input-group-text">วัน</span>
                                </div>
                              </div>
                              <div className="d-flex flex-wrap mt-2" style={{ gap: "4px" }}>
                                {[7, 14, 21, 28, 35, 60].map(d => (
                                  <button
                                    key={d}
                                    type="button"
                                    className="btn btn-outline-primary btn-sm py-0 px-2"
                                    style={{ fontSize: "0.78rem", borderRadius: "12px" }}
                                    onClick={() => setCalcDays(d)}
                                  >
                                    {d} วัน {d === 35 ? "(1ด.+1ส.)" : d === 28 ? "(1ด.)" : ""}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="row mb-3">
                              <div className="col-6">
                                <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem" }}>
                                  วันที่เริ่มเข้าพัก (พ.ศ.):
                                </label>
                                <Datetime
                                  value={calcStartDate && moment(calcStartDate).isValid() ? moment(calcStartDate) : null}
                                  renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                                  inputProps={{
                                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                                    readOnly: "readonly",
                                    style: { cursor: "pointer", backgroundColor: "#ffffff", fontSize: "0.85rem" },
                                    className: "form-control form-control-sm",
                                    value: calcStartDate && moment(calcStartDate).isValid() ? moment(calcStartDate).format("D MMMM BBBB") : ""
                                  }}
                                  closeOnSelect={true}
                                  closeOnTab={true}
                                  dateFormat="D MMMM BBBB"
                                  timeFormat={false}
                                  onChange={(val) => {
                                    if (moment.isMoment(val) && val.isValid()) {
                                      setCalcStartDate(val.format("YYYY-MM-DD"));
                                    } else if (val && typeof val === "string") {
                                      const m = moment(val);
                                      if (m.isValid()) setCalcStartDate(m.format("YYYY-MM-DD"));
                                    } else if (!val) {
                                      setCalcStartDate("");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-6">
                                <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem" }}>
                                  วันที่สิ้นสุด (พ.ศ.):
                                </label>
                                <Datetime
                                  value={calcEndDate && moment(calcEndDate).isValid() ? moment(calcEndDate) : null}
                                  renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                                  inputProps={{
                                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                                    readOnly: "readonly",
                                    style: { cursor: "pointer", backgroundColor: "#ffffff", fontSize: "0.85rem" },
                                    className: "form-control form-control-sm",
                                    value: calcEndDate && moment(calcEndDate).isValid() ? moment(calcEndDate).format("D MMMM BBBB") : ""
                                  }}
                                  closeOnSelect={true}
                                  closeOnTab={true}
                                  dateFormat="D MMMM BBBB"
                                  timeFormat={false}
                                  onChange={(val) => {
                                    if (moment.isMoment(val) && val.isValid()) {
                                      setCalcEndDate(val.format("YYYY-MM-DD"));
                                    } else if (val && typeof val === "string") {
                                      const m = moment(val);
                                      if (m.isValid()) setCalcEndDate(m.format("YYYY-MM-DD"));
                                    } else if (!val) {
                                      setCalcEndDate("");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-12 mt-2">
                                <span style={{ fontSize: "0.84rem", color: "#0369a1", fontWeight: "700" }}>
                                  ⏱️ คำนวณได้: {effectiveDays} วัน
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Paid Amount Input */}
                          <div className="pt-2" style={{ borderTop: "1px dashed #cbd5e1" }}>
                            <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem", color: "#0f172a" }}>
                              💵 ยอดเงินที่ผู้เข้าพักจ่ายมาแล้ว (Paid Amount):
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                min="0"
                                className="form-control font-weight-bold"
                                style={{ fontSize: "1.1rem", color: "#065f46" }}
                                value={calcPaidAmount}
                                onChange={(e) => setCalcPaidAmount(e.target.value)}
                                placeholder="เช่น 500, 625"
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">บาท</span>
                              </div>
                            </div>
                            <div className="d-flex mt-2" style={{ gap: "6px" }}>
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm py-0 px-2"
                                style={{ fontSize: "0.78rem", borderRadius: "10px" }}
                                onClick={() => setCalcPaidAmount(calcResult.totalDue.toString())}
                              >
                                ชำระเต็ม ({calcResult.totalDue.toLocaleString()} บ.)
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm py-0 px-2"
                                style={{ fontSize: "0.78rem", borderRadius: "10px" }}
                                onClick={() => setCalcPaidAmount("0")}
                              >
                                ยังไม่ชำระ (0 บ.)
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Realtime Result & Balance Card */}
                      <div className="col-lg-6 mb-3">
                        <div
                          style={{
                            backgroundColor: "#f0fdf4",
                            border: "2px solid #16a34a",
                            borderRadius: "10px",
                            padding: "18px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between"
                          }}
                        >
                          <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                              <span style={{ fontSize: "0.86rem", fontWeight: "800", color: "#166534" }}>
                                📊 ผลการคำนวณตามระเบียบ Proration
                              </span>
                              <Badge color="success" pill style={{ fontSize: "0.8rem", padding: "4px 10px" }}>
                                {calcBuilding === "2" ? "แพทย์ ตึก 2" : "ตึก 1, 3, 4"}
                              </Badge>
                            </div>

                            {/* Formula breakdown */}
                            <div style={{ backgroundColor: "#ffffff", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px" }}>
                              <div style={{ fontSize: "0.8rem", color: "#065f46", fontWeight: "700" }}>
                                การแจกแจงระยะเวลา ({effectiveDays} วัน):
                              </div>
                              <div style={{ fontSize: "0.95rem", fontWeight: "800", color: "#0f172a", marginTop: "2px" }}>
                                {calcResult.breakdownText}
                              </div>
                            </div>

                            {/* Total Due Big Callout */}
                            <div style={{ backgroundColor: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
                              <div style={{ fontSize: "0.82rem", color: "#047857", fontWeight: "700" }}>
                                ยอดที่ต้องเรียกเก็บทั้งสิ้น (Total Due):
                              </div>
                              <div style={{ fontSize: "2rem", fontWeight: "900", color: "#065f46", lineHeight: "1.1", marginTop: "4px" }}>
                                {calcResult.totalDue.toLocaleString()} <span style={{ fontSize: "1.1rem", fontWeight: "700" }}>บาท</span>
                              </div>
                            </div>

                            {/* Payment Balance Status Box */}
                            <div
                              style={{
                                backgroundColor: balanceResult.isFullyPaid ? "#dcfce7" : balanceResult.isPartial ? "#fef3c7" : "#fee2e2",
                                border: `2px solid ${balanceResult.isFullyPaid ? "#22c55e" : balanceResult.isPartial ? "#f59e0b" : "#ef4444"}`,
                                borderRadius: "8px",
                                padding: "12px 16px"
                              }}
                            >
                              <div style={{ fontSize: "0.8rem", fontWeight: "800", color: balanceResult.isFullyPaid ? "#166534" : balanceResult.isPartial ? "#92400e" : "#991b1b" }}>
                                {balanceResult.isFullyPaid
                                  ? "✓ ชำระครบถ้วนพอดี (PAID)"
                                  : balanceResult.isPartial
                                  ? "⚠️ ชำระบางส่วน (PARTIALLY PAID)"
                                  : "⏳ ค้างชำระเต็มจำนวน (UNPAID)"}
                              </div>
                              <div style={{ fontSize: "1.25rem", fontWeight: "900", color: balanceResult.isFullyPaid ? "#15803d" : balanceResult.isPartial ? "#b45309" : "#dc2626", marginTop: "4px" }}>
                                {balanceResult.isFullyPaid
                                  ? "ยอดคงค้าง: 0.00 บาท"
                                  : `ยอดค้างชำระ: ${balanceResult.remaining.toLocaleString()} บาท`}
                              </div>
                              <div style={{ fontSize: "0.82rem", color: "#334155", marginTop: "4px" }}>
                                (จ่ายมาแล้ว {balanceResult.paid.toLocaleString()} บาท จากยอดเรียกเก็บ {calcResult.totalDue.toLocaleString()} บาท)
                              </div>
                              {balanceResult.overpaid > 0 && (
                                <div style={{ fontSize: "0.84rem", color: "#0369a1", fontWeight: "800", marginTop: "4px" }}>
                                  💵 ชำระเกิน: ทอน {balanceResult.overpaid.toLocaleString()} บาท
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 pt-2 text-right" style={{ borderTop: "1px dashed #86efac" }}>
                            <button
                              type="button"
                              className="btn btn-success btn-sm font-weight-bold"
                              style={{ borderRadius: "8px" }}
                              onClick={handleCopySummary}
                            >
                              <FileCopyIcon style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }} />
                              {copySuccess ? "✓ คัดลอกสำเร็จ!" : "คัดลอกข้อมูลสรุป"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Add / Edit Master Data Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {modalMode === "ROOM" && (isEditing ? "✏️ แก้ไขข้อมูลห้องพัก" : "➕ เพิ่มห้องพักใหม่")}
          {modalMode === "INST" && "เพิ่มสถาบันการศึกษา"}
          {modalMode === "DEPT" && "เพิ่มกลุ่มงานที่รับฝึก"}
        </ModalHeader>
        <ModalBody>
          {modalMode === "ROOM" && (
            <div>
              <div className="mb-3">
                <label className="form-label font-weight-bold">เลือกอาคาร / ตึกหอพัก *</label>
                <select
                  className="form-control"
                  value={itemForm.buildingName || "ตึก 1"}
                  onChange={(e) => setItemForm({ ...itemForm, buildingName: e.target.value })}
                >
                  <option value="ตึก 1">🏢 อาคารหอพัก 1 (ตึก 1)</option>
                  <option value="ตึก 2">🏢 อาคารหอพัก 2 (ตึก 2)</option>
                  <option value="ตึก 3">🏢 อาคารหอพัก 3 (ตึก 3)</option>
                  <option value="ตึก 4">🏢 อาคารหอพัก 4 (ตึก 4)</option>
                </select>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label font-weight-bold">ชั้น *</label>
                  <select
                    className="form-control"
                    value={itemForm.floor || 1}
                    onChange={(e) => setItemForm({ ...itemForm, floor: Number(e.target.value) })}
                  >
                    <option value={1}>ชั้น 1</option>
                    <option value={2}>ชั้น 2</option>
                    <option value={3}>ชั้น 3</option>
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label font-weight-bold">หมายเลขห้องพัก *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น 101, 112, 231"
                    value={itemForm.roomNo || ""}
                    onChange={(e) => setItemForm({ ...itemForm, roomNo: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">ความจุเตียง</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={itemForm.capacity || 2}
                    onChange={(e) => setItemForm({ ...itemForm, capacity: Number(e.target.value) })}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label font-weight-bold">ประเภทห้องและกลุ่มผู้พัก *</label>
                  <select
                    className="form-control"
                    value={getRoomCategoryKey(itemForm)}
                    onChange={(e) => {
                      const nextCatKey = e.target.value;
                      setItemForm((prev) => applyRoomCategory(prev, nextCatKey));
                    }}
                    style={{ fontWeight: "600" }}
                  >
                    {ROOM_CATEGORY_LIST.map((cat) => (
                      <option key={cat.key} value={cat.key}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1">
                    {getRoomCategoryInfo(itemForm).description}
                  </small>
                </div>
              </div>

              {/* อัตราค่าเช่า: หากเลือกเป็นนักศึกษาหรือแพทย์ แสดงอัตราค่าเช่า (บาท/ต่อคน) */}
              {(() => {
                const cat = getRoomCategoryInfo(itemForm);
                const isFeeApplicable = cat.key === "STUDENT" || cat.key === "DOCTOR";
                if (isFeeApplicable) {
                  return (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label font-weight-bold">อัตราค่าเช่ารายเดือน (บาท / ต่อคน) *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={itemForm.fee || 500}
                          onChange={(e) => setItemForm({ ...itemForm, fee: Number(e.target.value), monthlyRate: Number(e.target.value) })}
                        />
                        <small className="text-success font-weight-bold d-block mt-1">
                          💡 500 บาท/คน/เดือน (อยู่คนเดียวก็คิด 500 บาท)
                        </small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label font-weight-bold">อัตราค่าเช่ารายวัน (บาท / ต่อคน)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={itemForm.dailyFee || 100}
                          onChange={(e) => setItemForm({ ...itemForm, dailyFee: Number(e.target.value), dailyRate: Number(e.target.value) })}
                        />
                        <small className="text-muted d-block mt-1">100 บาท/คน/วัน</small>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="alert alert-light border py-2 px-3 mb-3" style={{ fontSize: "0.85rem", color: "#475569" }}>
                    ℹ️ <b>ห้องพักสวัสดิการ รพ. / ส่วนกลาง:</b> ไม่มีค่าเช่ารายเดือนและรายวัน (คิดเฉพาะค่าไฟตามมิเตอร์จริง)
                  </div>
                );
              })()}

              <div className="mb-3">
                <label className="form-label font-weight-bold">สถานะห้องพัก</label>
                <select
                  className="form-control"
                  value={itemForm.status === "MAINTENANCE" ? "MAINTENANCE" : "AVAILABLE"}
                  onChange={(e) => {
                    const nextStatus = e.target.value;
                    const occ = Number(itemForm.occupancy ?? (itemForm.residents ? itemForm.residents.length : 0));
                    if (nextStatus === "MAINTENANCE" && occ > 0) {
                      alert(
                        `⚠️ ไม่สามารถปิดปรับปรุงได้ในขณะนี้\n\n` +
                        `ห้อง ${itemForm.roomNo || ""} ยังมีผู้พักอาศัยอยู่จำนวน ${occ} คน\n` +
                        `กรุณาดำเนินการกดย้ายออก (Check-out) ผู้พักให้เรียบร้อยก่อน เพื่อเช็คว่าปิดปรับปรุงจริงไหม`
                      );
                      return;
                    }
                    const cap = Number(itemForm.capacity || 2);
                    setItemForm({
                      ...itemForm,
                      status: nextStatus === "MAINTENANCE" ? "MAINTENANCE" : (occ >= cap ? "OCCUPIED" : "AVAILABLE")
                    });
                  }}
                  style={{
                    borderColor: itemForm.status === "MAINTENANCE" ? "#f57c00" : "#2e7d32",
                    fontWeight: "bold"
                  }}
                >
                  <option value="AVAILABLE">🟢 เปิดใช้งานปกติ (ระบบคำนวณสถานะว่าง/ไม่ว่างอัตโนมัติตามผู้เข้าพัก)</option>
                  <option value="MAINTENANCE">🔧 ปิดปรับปรุง (งดใช้งานชั่วคราว)</option>
                </select>

                {(() => {
                  const occ = Number(itemForm.occupancy ?? (itemForm.residents ? itemForm.residents.length : 0));
                  const cap = Number(itemForm.capacity || 2);
                  if (itemForm.status === "MAINTENANCE") {
                    return (
                      <div className="alert alert-secondary mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: "0.85rem", borderRadius: "6px" }}>
                        <span>🔧 <b>ห้องปิดปรับปรุง:</b> งดให้จัดคนเข้าพักชั่วคราว</span>
                      </div>
                    );
                  }
                  if (occ === 0) {
                    return (
                      <div className="alert alert-success mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: "0.85rem", borderRadius: "6px" }}>
                        <span>✅ <b>สถานะห้อง:</b> ว่าง (ยังไม่มีผู้เข้าพัก จัดได้สูงสุด {cap} คน)</span>
                      </div>
                    );
                  }
                  if (occ < cap) {
                    return (
                      <div className="alert alert-warning mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: "0.85rem", borderRadius: "6px" }}>
                        <span>👥 <b>สถานะห้อง:</b> พักอยู่ {occ} คน (ว่างอีก {cap - occ} คน)</span>
                      </div>
                    );
                  }
                  return (
                    <div className="alert alert-danger mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: "0.85rem", borderRadius: "6px" }}>
                      <span>⛔ <b>สถานะห้อง:</b> ไม่ว่าง (มีผู้พักเต็มจำนวนเตียง {occ}/{cap} คน)</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {modalMode === "INST" && (
            <div>
              <div className="mb-3">
                <label className="form-label font-weight-bold">ชื่อสถาบันการศึกษา / หน่วยงาน *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น มหาวิทยาลัยทักษิณ"
                  value={itemForm.name || ""}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">ประเภทสถาบัน</label>
                <select
                  className="form-control"
                  value={itemForm.type || "มหาวิทยาลัย"}
                  onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })}
                >
                  <option value="มหาวิทยาลัย">มหาวิทยาลัย</option>
                  <option value="โรงพยาบาล">โรงพยาบาล</option>
                  <option value="วิทยาลัย">วิทยาลัย</option>
                  <option value="โรงเรียน">โรงเรียน</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">จังหวัด</label>
                <input
                  type="text"
                  className="form-control"
                  value={itemForm.province || "ตรัง"}
                  onChange={(e) => setItemForm({ ...itemForm, province: e.target.value })}
                />
              </div>
            </div>
          )}

          {modalMode === "DEPT" && (
            <div>
              <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
                ℹ️ ข้อมูลกลุ่มงานจะเชื่อมโยงกับฐานข้อมูลกลุ่มงานหลักของโรงพยาบาล (dbo.Department)
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label font-weight-bold">รหัสกลุ่มงาน</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น MED-02, DENT-01"
                    value={itemForm.code || ""}
                    onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })}
                  />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label font-weight-bold">ชื่อกลุ่มงานใน รพ. *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น กลุ่มงานทันตกรรม, กลุ่มงานรังสีวิทยา"
                    value={itemForm.name || ""}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">ชื่อย่อ / เรียกสั้นๆ</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น ทันตกรรม, เอกซเรย์"
                    value={itemForm.shortName || ""}
                    onChange={(e) => setItemForm({ ...itemForm, shortName: e.target.value })}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">สีสัญลักษณ์ประจำกลุ่มงาน</label>
                  <div className="d-flex align-items-center">
                    <input
                      type="color"
                      className="form-control form-control-color mr-2"
                      style={{ width: "45px", height: "38px", padding: "2px" }}
                      value={itemForm.color || "#00838f"}
                      onChange={(e) => setItemForm({ ...itemForm, color: e.target.value })}
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={itemForm.color || "#00838f"}
                      onChange={(e) => setItemForm({ ...itemForm, color: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label font-weight-bold">กลุ่มภารกิจ / ด้านที่สังกัด</label>
                <select
                  className="form-control"
                  value={itemForm.groupWorkName || "กลุ่มภารกิจด้านบริการทางการแพทย์"}
                  onChange={(e) => setItemForm({ ...itemForm, groupWorkName: e.target.value })}
                >
                  <option value="กลุ่มภารกิจด้านบริการทางการแพทย์">กลุ่มภารกิจด้านบริการทางการแพทย์</option>
                  <option value="กลุ่มภารกิจด้านการพยาบาล">กลุ่มภารกิจด้านการพยาบาล</option>
                  <option value="กลุ่มภารกิจด้านอำนวยการ">กลุ่มภารกิจด้านอำนวยการ</option>
                  <option value="กลุ่มภารกิจด้านวิชาการและการแพทย์">กลุ่มภารกิจด้านวิชาการและการแพทย์</option>
                  <option value="กลุ่มภารกิจด้านพัฒนาระบบบริการสุขภาพ">กลุ่มภารกิจด้านพัฒนาระบบบริการสุขภาพ</option>
                </select>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>ยกเลิก</Button>
          <Button color="success" onClick={handleSave}>บันทึก</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
